import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, Unlock, Star, Send, MapPin, Tag, Trophy, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { BACKEND_URL, resolveAssetUrl } from '../config';
import { api, errorMessage } from '../lib/api';
import { toast } from '../components/ui/sonner';
import { trackQRScan } from '../utils/analytics';
import { getScanSessionId } from '../lib/scanSession';
import '../styles/coaster-animations.css';

// V5 Design Constants
const v5 = {
  colors: {
    navy: '#1e1b4b',
    cream: '#FEF7ED',
    white: '#FFFFFF',
    purple: '#8b5cf6',
    purpleDark: '#7c3aed',
    orange: '#f97316',
    orangeDark: '#ea580c',
    green: '#22c55e',
    textPrimary: '#1e1b4b',
    textSecondary: 'rgba(30, 27, 75, 0.7)',
    textMuted: 'rgba(30, 27, 75, 0.5)',
    textHint: 'rgba(30, 27, 75, 0.4)',
    borderLight: 'rgba(30, 27, 75, 0.05)',
  },
  fonts: {
    display: "'Faustina', Georgia, serif",
    body: "'DM Sans', sans-serif",
    mono: "'JetBrains Mono', monospace"
  }
};

const ScanNew = () => {
  const [searchParams] = useSearchParams();
  const location = searchParams.get('location');
  const hub_id = searchParams.get('hub_id');
  const isV2 = searchParams.get('v1') !== 'true'; // V2 is now default; append ?v1=true for legacy flow
  const { isAuthenticated, openAuthModal, claimDeal, user, refreshUser, setPostAuthRedirect } = useAuth();
  const navigate = useNavigate();

  const [business, setBusiness] = useState(null);
  const [hub, setHub] = useState(null);
  const [coasterDesign, setCoasterDesign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [alreadyClaimed, setAlreadyClaimed] = useState(false);
  const [showCoaster, setShowCoaster] = useState(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  // V2 triple flip states: 0=front, 1=unlock-message, 2=transition
  const [flipPhase, setFlipPhase] = useState(0);
  const [isNavigating, setIsNavigating] = useState(false);
  // Backwards compatibility
  const isFlipped = flipPhase > 0;

  // Location detection states
  const [userLocation, setUserLocation] = useState(null);
  const [locationPermission, setLocationPermission] = useState('pending');
  const [nearestHub, setNearestHub] = useState(null);
  const [detectingLocation, setDetectingLocation] = useState(false);

  // Record scan on page load (fire-and-forget)
  const recordScan = async () => {
    try {
      const sessionId = getScanSessionId();

      await api.post('/scan/record', {
        location_id: hub_id || null,
        session_id: sessionId,
        referral_source: hub_id ? 'qr_code' : 'direct',
        campaign_id: null,
        user_id: user?.id || null
      });
      trackQRScan({ hubId: hub_id || null, referralSource: hub_id ? 'qr_code' : 'direct' });
    } catch (error) {
      console.error('Error recording scan:', error);
    }
  };

  useEffect(() => {
    recordScan();
  }, []);

  useEffect(() => {
    if (!hub_id && locationPermission === 'pending') {
      requestLocationPermission();
    }
  }, [hub_id, locationPermission]);

  const requestLocationPermission = () => {
    if (!navigator.geolocation) {
      setLocationPermission('denied');
      return;
    }

    setDetectingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ lat: latitude, lng: longitude });
        setLocationPermission('granted');

        try {
          const response = await api.get('/hubs/nearest', {
            params: { lat: latitude, lng: longitude }
          });
          if (response.data) {
            setNearestHub(response.data);
          }
        } catch (error) {
          console.log('Could not find nearest hub:', error);
        }
        setDetectingLocation(false);
      },
      (error) => {
        setLocationPermission('denied');
        setDetectingLocation(false);
      },
      { timeout: 10000, enableHighAccuracy: false }
    );
  };

  useEffect(() => {
    const fetchHubAndCoaster = async () => {
      if (hub_id) {
        try {
          const response = await api.get('/hubs/with-deals');
          const hubs = response.data;
          const currentHub = hubs.find(h => String(h.id) === String(hub_id));
          if (currentHub) {
            setHub(currentHub);
          }

          try {
            const coasterResponse = await api.get(`/hubs/${hub_id}/coaster`);
            const coasterData = coasterResponse.data;

            if (coasterData.coaster_image_url) {
              coasterData.coaster_image_url = resolveAssetUrl(coasterData.coaster_image_url);
            }
            if (coasterData.coaster_back_url) {
              coasterData.coaster_back_url = resolveAssetUrl(coasterData.coaster_back_url);
            }

            setCoasterDesign(coasterData);
          } catch (coasterError) {
            console.error('[COASTER] Failed to fetch coaster design:', coasterError);
          }
        } catch (error) {
          console.error('Error fetching hub:', error);
        }
      }
    };
    fetchHubAndCoaster();
  }, [hub_id]);

  // V1: Fetch single deal for old flow
  useEffect(() => {
    if (hub_id && !isV2) {
      fetchBusiness();
    } else if (!hub_id) {
      setLoading(false);
    } else if (isV2) {
      // V2 doesn't need to fetch a single deal
      setLoading(false);
    }
  }, [hub_id, isV2]);

  useEffect(() => {
    const checkClaim = async () => {
      if (isAuthenticated && user && business?.deal_id) {
        try {
          const response = await api.get(`/deals/check/${business.deal_id}`);
          setAlreadyClaimed(response.data.claimed);
        } catch (error) {
          console.error('Error checking claim:', error);
        }
      }
    };
    checkClaim();
  }, [business, isAuthenticated, user]);

  // V2: Triple flip sequence orchestration
  // Phase 0 → 1: After entrance animation (1.8s), show "X Deals here" message
  useEffect(() => {
    if (!isV2 || !hub_id || !coasterDesign || loading || flipPhase > 0) return;

    const flip1Timer = setTimeout(() => {
      setFlipPhase(1);
    }, 1800);

    return () => clearTimeout(flip1Timer);
  }, [isV2, hub_id, coasterDesign, loading, flipPhase]);

  // Phase 1 → 2: After 1.5s hold, show "Taking you there..." message
  useEffect(() => {
    if (flipPhase !== 1) return;

    const flip2Timer = setTimeout(() => {
      setFlipPhase(2);
    }, 1500);

    return () => clearTimeout(flip2Timer);
  }, [flipPhase]);

  // Phase 2 → Navigate: After 0.8s hold, navigate to deals page
  useEffect(() => {
    if (flipPhase !== 2 || isNavigating) return;

    const navigateTimer = setTimeout(() => {
      setIsNavigating(true);
      handleNavigateToDeals();
    }, 800);

    return () => clearTimeout(navigateTimer);
  }, [flipPhase, isNavigating]);

  const fetchBusiness = async () => {
    try {
      setLoading(true);

      // /scan/discover returns a random deal at the venue; if the signed-in
      // patron already claimed it we retry ONCE. Ten attempts meant up to
      // twenty sequential round trips before first paint (audit row 102) —
      // the real fix is server-side exclusion, tracked in the report.
      const MAX_ATTEMPTS = 2;
      let foundUnclaimed = false;
      let attempts = 0;
      let businessData = null;

      while (!foundUnclaimed && attempts < MAX_ATTEMPTS) {
        attempts++;
        const locationParam = hub_id || location;
        const params = new URLSearchParams();

        if (locationParam) {
          params.append('location', locationParam);
        }
        if (hub_id) {
          params.append('hub_id', hub_id);
        }

        const query = params.toString();
        const response = await api.get(`/scan/discover${query ? `?${query}` : ''}`);
        businessData = response.data;

        if (isAuthenticated && user) {
          try {
            const checkResponse = await api.get(`/deals/check/${businessData.deal_id}`);
            foundUnclaimed = !checkResponse.data.claimed;
          } catch (error) {
            foundUnclaimed = true;
          }
        } else {
          foundUnclaimed = true;
        }
      }

      setBusiness(businessData);
    } catch (err) {
      console.error('Error fetching business:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCoasterClick = () => {
    setShowCoaster(false);
  };

  // V2: Navigate to deals page (auto-called after triple flip)
  const handleNavigateToDeals = async () => {
    if (!hub_id) {
      navigate('/local-deals-near-me');
      return;
    }

    if (isAuthenticated) {
      // Authenticated: Unlock hub permanently and navigate with ?unlock=true
      try {
        await api.post(`/hubs/unlock/${hub_id}`, {});
        if (refreshUser) {
          await refreshUser();
        }
      } catch (error) {
        console.error('Error unlocking hub:', error);
        // Still navigate — hub may already be unlocked
      }
      navigate(`/local-deals-near-me?hub_id=${hub_id}`);
    } else {
      // Unauthenticated: Store preview info and navigate with ?preview=true
      sessionStorage.setItem('preview_hub_id', hub_id);
      sessionStorage.setItem('preview_hub_name', hub?.name || coasterDesign?.hub_name || 'this venue');
      navigate(`/local-deals-near-me?hub_id=${hub_id}&preview=true`);
    }
  };

  // V2: Manual unlock button (fallback if user clicks before auto-navigation)
  const handleUnlockAllOffersV2 = async () => {
    if (isNavigating) return; // Prevent double navigation
    setIsNavigating(true);
    await handleNavigateToDeals();
  };

  // V1: Original unlock handler
  const handleUnlockAllOffers = async () => {
    if (!isAuthenticated) {
      openAuthModal('signup');
      return;
    }

    if (!hub_id) {
      navigate('/local-deals-near-me');
      return;
    }

    setUnlocking(true);
    try {
      await api.post(`/hubs/unlock/${hub_id}`, {});

      if (refreshUser) {
        await refreshUser();
      }

      navigate('/local-deals-near-me');
    } catch (error) {
      toast.error(errorMessage(error, 'Could not open the deals at this venue. Please try again.'));
    } finally {
      setUnlocking(false);
    }
  };

  const handleClaim = async () => {
    if (!isAuthenticated) {
      openAuthModal('signup');
      return;
    }

    if (!business) return;

    setClaiming(true);

    try {
      const dealData = {
        business_id: business.business_id,
        deal_id: business.deal_id,
        business_name: business.business_name,
        business_slug: business.business_slug,
        deal_title: business.deal_title,
        deal_description: business.deal_description
      };

      const result = await claimDeal(dealData);

      if (result.success) {
        setAlreadyClaimed(true);
        setTimeout(() => {
          navigate('/deal-wallet');
        }, 1500);
      } else {
        toast.error(result.error || 'We could not claim that deal. Please try again.');
      }
    } catch (error) {
      toast.error(errorMessage(error, 'We could not claim that deal. Please try again.'));
    } finally {
      setClaiming(false);
    }
  };

  const handleShareViaSMS = () => {
    if (!business) return;

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const separator = isIOS ? '&' : '?';

    const userId = user?.id || 'guest';
    const shareLink = `${window.location.origin}/scan?location=${business.business_slug}&ref=${userId}&src=sms`;

    const message = `You need this: ${business.business_name} - ${business.deal_title}

Expires tonight → ${shareLink}

(Found it at ${hubName})`;

    const smsUrl = `sms:${separator}body=${encodeURIComponent(message)}`;
    window.location.href = smsUrl;
  };

  const hubName = hub?.name || nearestHub?.name || 'this location';

  const coasterImgSrc = coasterDesign?.coaster_image_url || `${BACKEND_URL}/api/files/coasters/default-coaster.png`;
  const coasterAlt = coasterDesign?.hub_name ? `${coasterDesign.hub_name} Coaster` : "Tappd Coaster";
  const coasterFallback = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIiB2aWV3Qm94PSIwIDAgMzAwIDMwMCI+PHJlY3Qgd2lkdGg9IjMwMCIgaGVpZ2h0PSIzMDAiIGZpbGw9IiMxZTFiNGIiIHJ4PSIyNCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjZmVmN2VkIiBmb250LWZhbWlseT0iR2VvcmdpYSwgc2VyaWYiIGZvbnQtc2l6ZT0iNDAiIGZvbnQtd2VpZ2h0PSJib2xkIj5UYXBWZC48L3RleHQ+PC9zdmc+';

  // Loading state
  if (loading && !business && !(isV2 && hub_id)) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: v5.colors.cream }}>
        <div
          className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent"
          style={{ borderColor: v5.colors.purple, borderTopColor: 'transparent' }}
        ></div>
      </div>
    );
  }

  // No hub_id - Location detection UI (shared by v1 and v2)
  if (!hub_id && !business) {
    return (
      <div style={{
        backgroundImage: 'url(/scan-background.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        flexDirection: 'column'
      }} className="min-h-screen-dynamic">
        <div className="pt-8 pb-4 text-center">
          <Link to="/" aria-label="Tappd home" className="inline-block mb-4">
            <img
              src="/tappd-logo-white.png"
              alt="Tappd"
              className="mx-auto"
              style={{ height: '50px' }}
            />
          </Link>
          <p style={{ fontFamily: v5.fonts.mono, fontSize: '12px', color: 'rgba(255, 255, 255, 0.4)', marginBottom: '8px' }}>
            // discover_deals
          </p>
          <h2 className="text-2xl md:text-3xl px-4" style={{
            fontFamily: "'Faustina', Georgia, serif",
            fontWeight: 500,
            color: v5.colors.white
          }}>
            {detectingLocation
              ? 'Finding Tappd Locations Near You...'
              : nearestHub
                ? `${nearestHub.name} is ${nearestHub.distance_text}!`
                : 'Discover Local Deals Near You'}
          </h2>
        </div>

        <div className="flex-1 flex items-center justify-center px-6">
          <div className="w-full max-w-md">

            {detectingLocation && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center"
              >
                <div
                  className="w-16 h-16 mx-auto mb-4 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
                >
                  <MapPin size={32} className="text-white animate-pulse" />
                </div>
                <p style={{ fontFamily: v5.fonts.body, color: 'rgba(255, 255, 255, 0.8)' }}>
                  Please allow location access to find deals near you
                </p>
              </motion.div>
            )}

            {!detectingLocation && nearestHub && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
              >
                <div
                  className="rounded-xl p-6 mb-6"
                  style={{
                    backgroundColor: v5.colors.white,
                    border: `1px solid ${v5.colors.borderLight}`,
                    boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)',
                    borderLeft: `4px solid ${v5.colors.green}`
                  }}
                >
                  <p style={{ fontFamily: v5.fonts.mono, fontSize: '12px', color: v5.colors.textHint, marginBottom: '12px' }}>
                    // nearest_hub
                  </p>
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <MapPin size={20} style={{ color: v5.colors.green }} />
                    <span style={{ fontFamily: v5.fonts.body, fontWeight: 600, color: v5.colors.green }}>
                      {nearestHub.distance_text}
                    </span>
                  </div>
                  <h3 style={{ fontFamily: v5.fonts.display, fontSize: '20px', fontWeight: 500, color: v5.colors.textPrimary, marginBottom: '8px' }}>
                    {nearestHub.name}
                  </h3>
                  <p style={{ fontFamily: v5.fonts.body, fontSize: '14px', color: v5.colors.textMuted, marginBottom: '16px' }}>
                    {nearestHub.city}, {nearestHub.state}
                  </p>
                  <Button
                    onClick={() => navigate(`/scan?hub_id=${nearestHub.id}`)}
                    className="w-full py-3 rounded-xl font-semibold text-white transition-all hover:-translate-y-0.5"
                    style={{
                      background: `linear-gradient(135deg, ${v5.colors.orange} 0%, ${v5.colors.orangeDark} 100%)`,
                      fontFamily: v5.fonts.body,
                      boxShadow: '0 4px 15px rgba(249, 115, 22, 0.4)'
                    }}
                  >
                    View Deals at {nearestHub.name}
                  </Button>
                </div>

                <button
                  onClick={() => navigate('/local-deals-near-me')}
                  className="hover:underline"
                  style={{ fontFamily: v5.fonts.body, color: 'rgba(255, 255, 255, 0.8)' }}
                >
                  Or explore all Tappd locations
                </button>
              </motion.div>
            )}

            {!detectingLocation && locationPermission === 'denied' && !nearestHub && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
              >
                <div
                  className="w-20 h-20 mx-auto mb-6 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
                >
                  <MapPin size={40} className="text-white" />
                </div>
                <p style={{ fontFamily: v5.fonts.body, fontSize: '18px', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '24px' }}>
                  Scan a Tappd coaster at a participating venue, or explore all locations
                </p>
                <Button
                  onClick={() => navigate('/local-deals-near-me')}
                  className="w-full py-4 rounded-xl font-semibold text-white text-lg transition-all hover:-translate-y-0.5"
                  style={{
                    background: `linear-gradient(135deg, ${v5.colors.orange} 0%, ${v5.colors.orangeDark} 100%)`,
                    fontFamily: v5.fonts.body,
                    boxShadow: '0 4px 15px rgba(249, 115, 22, 0.4)'
                  }}
                >
                  Explore Tappd Locations Near You
                </Button>

                <button
                  onClick={requestLocationPermission}
                  className="mt-4 hover:underline text-sm"
                  style={{ fontFamily: v5.fonts.body, color: 'rgba(255, 255, 255, 0.7)' }}
                >
                  Try enabling location again
                </button>
              </motion.div>
            )}

          </div>
        </div>
      </div>
    );
  }

  // ===================================================
  // V2 FLOW: Auto-flip coaster → See All Deals CTA
  // ===================================================
  if (isV2 && hub_id) {
    return (
      <div style={{
        backgroundImage: 'url(/scan-background.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        flexDirection: 'column'
      }} className="min-h-screen-dynamic">
        {/* Logo & Header */}
        <div className="pt-4 pb-2 text-center">
          {(() => {
            const hubLogoSrc = coasterDesign?.hub_logo_url
              ? resolveAssetUrl(coasterDesign.hub_logo_url)
              : hub?.logo_url
                ? resolveAssetUrl(hub.logo_url)
                : hub?.image_url
                  ? resolveAssetUrl(hub.image_url)
                  : null;
            return hubLogoSrc ? (
              <img
                src={hubLogoSrc}
                alt={hub?.name || coasterDesign?.hub_name || 'Venue'}
                className="mx-auto mb-4"
                style={{
                  height: '50px',
                  maxWidth: '180px',
                  objectFit: 'contain',
                  borderRadius: '8px'
                }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/tappd-logo-white.png';
                  e.target.style.borderRadius = '0';
                }}
              />
            ) : (
              <Link to="/" aria-label="Tappd home" className="inline-block mb-4">
                <img
                  src="/tappd-logo-white.png"
                  alt="Tappd"
                  className="mx-auto"
                  style={{ height: '50px' }}
                />
              </Link>
            );
          })()}
          <h2 className="text-2xl md:text-3xl px-4" style={{
            fontFamily: "'Faustina', Georgia, serif",
            fontWeight: 500,
            color: v5.colors.white
          }}>
            {isFlipped ? `${hub?.deal_count || ''} Deals at ${hubName}` : `Welcome to ${hubName}`}
          </h2>
          <p className="text-base mt-2" style={{
            fontFamily: v5.fonts.body,
            color: v5.colors.orange
          }}>
            {isFlipped ? 'Local deals from businesses nearby' : 'Powered by Tappd'}
          </p>

          {/* Value Props — compact row, always visible */}
          <div className="flex items-center justify-center gap-5 mt-3">
            <div className="flex items-center gap-1.5">
              <Tag size={14} style={{ color: v5.colors.orange }} />
              <span style={{ fontFamily: v5.fonts.body, fontSize: '12px', fontWeight: 600, color: 'rgba(255, 255, 255, 0.85)' }}>Deals</span>
            </div>
          </div>
        </div>

        {/* Main Content — Flip Card */}
        <div className="flex-1 flex items-center justify-center px-6 pb-2">
          <div className="w-full max-w-[340px]">
            <motion.div
              initial={{
                x: -400,
                rotate: -15,
                scale: 0.9,
                opacity: 0
              }}
              animate={{
                x: 0,
                rotate: 0,
                scale: 1,
                opacity: 1
              }}
              transition={{
                duration: 1.2,
                ease: [0.25, 0.46, 0.45, 0.94],
                x: { type: "spring", stiffness: 100, damping: 20 }
              }}
              style={{
                animation: !isFlipped ? 'coasterFloat 3s ease-in-out infinite' : 'none'
              }}
              role="button"
              tabIndex={0}
              aria-label={`See all deals at ${hubName}`}
              onClick={handleUnlockAllOffersV2}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleUnlockAllOffersV2();
                }
              }}
              className="cursor-pointer"
            >
              <div className="coaster-flip-container">
                <div className={`coaster-flip-inner ${isFlipped ? 'flipped' : ''}`}>
                  {/* Front — Coaster Image */}
                  <div className="coaster-flip-front">
                    <motion.div
                      className="rounded-3xl pointer-events-none"
                      style={{ position: 'absolute', inset: 0 }}
                      animate={!isFlipped ? {
                        boxShadow: [
                          '0 0 30px rgba(139, 92, 246, 0.5)',
                          '0 0 50px rgba(139, 92, 246, 0.8)',
                          '0 0 30px rgba(139, 92, 246, 0.5)'
                        ]
                      } : {}}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                    <img
                      src={coasterImgSrc}
                      alt={coasterAlt}
                      className="w-full rounded-3xl shadow-2xl"
                      style={{
                        animation: !isFlipped ? 'coasterGlow 2s ease-in-out infinite alternate' : 'none'
                      }}
                      onError={(e) => {
                        if (!e.target.src.includes('placeholder')) {
                          e.target.src = coasterFallback;
                        }
                      }}
                    />
                  </div>

                  {/* Back — See All Deals CTA */}
                  <div
                    className="coaster-flip-back"
                    style={{
                      background: `linear-gradient(145deg, ${v5.colors.navy} 0%, #2d2a5d 60%, #3b2d6e 100%)`,
                      aspectRatio: '1 / 1',
                      boxShadow: '0 8px 40px rgba(30, 27, 75, 0.4)'
                    }}
                  >
                    <div className="shimmer-overlay" />
                    <div className="text-center p-8 relative z-10">
                      {/* Decorative pattern */}
                      <div
                        className="absolute inset-0 opacity-[0.04] pointer-events-none"
                        style={{
                          backgroundImage: `
                            linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)
                          `,
                          backgroundSize: '30px 30px'
                        }}
                      />

                      {/* PHASE 1: "X Deals here at [Hub]" */}
                      {flipPhase === 1 && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.4 }}
                          className="flex flex-col items-center justify-center h-full"
                        >
                          <motion.div
                            className="w-20 h-20 mx-auto mb-5 rounded-2xl flex items-center justify-center"
                            style={{
                              background: `linear-gradient(135deg, ${v5.colors.purple} 0%, ${v5.colors.orange} 100%)`,
                              boxShadow: '0 4px 20px rgba(139, 92, 246, 0.4)'
                            }}
                            animate={{ scale: [1, 1.1, 1] }}
                            transition={{ duration: 0.6, repeat: Infinity, repeatDelay: 0.5 }}
                          >
                            <Unlock size={36} className="text-white" />
                          </motion.div>

                          <h3
                            className="text-3xl font-bold text-white mb-2"
                            style={{ fontFamily: v5.fonts.display }}
                          >
                            {hub?.deal_count || 0} Deals here
                          </h3>
                          <p
                            className="text-lg"
                            style={{ fontFamily: v5.fonts.body, color: 'rgba(255, 255, 255, 0.7)' }}
                          >
                            at {hub?.name || coasterDesign?.hub_name || 'this venue'}
                          </p>
                        </motion.div>
                      )}

                      {/* PHASE 2: "Taking you there..." */}
                      {flipPhase === 2 && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3 }}
                          className="flex flex-col items-center justify-center h-full"
                        >
                          <motion.div
                            className="w-16 h-16 mx-auto mb-4 flex items-center justify-center"
                            animate={{ y: [0, -5, 0] }}
                            transition={{ duration: 0.8, repeat: Infinity }}
                          >
                            <MapPin size={48} style={{ color: v5.colors.orange }} />
                          </motion.div>

                          <h3
                            className="text-2xl font-bold text-white mb-4"
                            style={{ fontFamily: v5.fonts.display }}
                          >
                            Taking you there...
                          </h3>

                          {/* Animated loading dots */}
                          <div className="flex justify-center gap-2">
                            {[0, 1, 2].map((i) => (
                              <motion.div
                                key={i}
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: v5.colors.orange }}
                                animate={{ opacity: [0.3, 1, 0.3] }}
                                transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                              />
                            ))}
                          </div>
                        </motion.div>
                      )}

                      {/* The unflipped face shows the coaster art; the CTA below the
                          card stays reachable in every phase (audit row 97). */}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Always-reachable CTA — the flip sequence is a 4s animation, not a gate. */}
            <Button
              onClick={handleUnlockAllOffersV2}
              disabled={unlocking || isNavigating}
              className="w-full mt-6 py-4 min-h-[44px] rounded-xl font-bold text-lg transition-all hover:-translate-y-0.5"
              style={{
                background: `linear-gradient(135deg, ${v5.colors.orange} 0%, ${v5.colors.orangeDark} 100%)`,
                color: 'white',
                fontFamily: v5.fonts.body,
                boxShadow: '0 4px 20px rgba(249, 115, 22, 0.5)',
                opacity: (unlocking || isNavigating) ? 0.7 : 1
              }}
            >
              <Unlock size={20} className="mr-2 inline" aria-hidden="true" />
              {(unlocking || isNavigating) ? 'Loading...' : 'See All Deals'}
            </Button>
          </div>
        </div>

        {/* Tappd Copyright Footer */}
        <motion.div
          className="pb-4 pt-2 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: isFlipped ? 1 : 0 }}
          transition={{ duration: 0.5, delay: 1.6 }}
        >
          <img
            src="/tappd-logo-white.png"
            alt="Tappd"
            className="mx-auto mb-1"
            style={{ height: '18px', opacity: 0.5 }}
          />
          <p style={{
            fontFamily: v5.fonts.body,
            fontSize: '11px',
            color: 'rgba(255, 255, 255, 0.3)'
          }}>
            &copy; {new Date().getFullYear()} Tappd App
          </p>
        </motion.div>
      </div>
    );
  }

  // ===================================================
  // V1 FLOW: Original tap-to-reveal with deal card
  // ===================================================
  return (
    <div style={{
      backgroundImage: 'url(/scan-background.png)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      display: 'flex',
      flexDirection: 'column'
    }} className="min-h-screen-dynamic">
      {/* Logo & Header */}
      <div className="pt-4 pb-2 text-center">
        <Link to="/" aria-label="Tappd home" className="inline-block mb-4">
          <img
            src="/tappd-logo-white.png"
            alt="Tappd"
            className="mx-auto"
            style={{ height: '50px' }}
          />
        </Link>
        <p style={{ fontFamily: v5.fonts.mono, fontSize: '12px', color: 'rgba(255, 255, 255, 0.4)', marginBottom: '8px' }}>
          // {showCoaster ? 'tap_to_reveal' : 'your_offer'}
        </p>
        <h2 className="text-2xl md:text-3xl" style={{
          fontFamily: "'Faustina', Georgia, serif",
          fontWeight: 500,
          color: v5.colors.white
        }}>
          {showCoaster ? 'Tap the coaster to see the deals at this venue' : 'Your Deal'}
        </h2>
        <p className="text-base mt-2" style={{
          fontFamily: v5.fonts.body,
          color: v5.colors.orange
        }}>
          Supported by {hubName}
        </p>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-6 pb-2">
        <div className="w-full max-w-[340px]">
          <AnimatePresence mode="wait">
            {showCoaster ? (
              /* COASTER */
              <motion.div
                key="coaster"
                initial={isFirstLoad ? (() => {
                  const fromLeft = Math.random() > 0.5;
                  const randomRotation = Math.random() * 20 - 10;

                  return {
                    x: fromLeft ? -500 : 500,
                    y: Math.random() * 50 - 25,
                    rotate: randomRotation,
                    scale: 0.9,
                    opacity: 0
                  };
                })() : { opacity: 1 }}
                animate={{
                  x: 0,
                  y: 0,
                  rotate: -5,
                  scale: 1,
                  opacity: 1
                }}
                exit={{
                  scale: 0.8,
                  opacity: 0
                }}
                transition={{
                  duration: isFirstLoad ? 1.2 : 0.5,
                  ease: isFirstLoad ? [0.25, 0.46, 0.45, 0.94] : "easeOut",
                  x: {
                    type: isFirstLoad ? "spring" : "tween",
                    stiffness: 100,
                    damping: 20
                  }
                }}
                onClick={handleCoasterClick}
                className="cursor-pointer relative"
              >
                {/* Pulsing Glow */}
                <motion.div
                  className="absolute inset-0 rounded-3xl pointer-events-none"
                  animate={{
                    boxShadow: [
                      `0 0 30px rgba(139, 92, 246, 0.5)`,
                      `0 0 50px rgba(139, 92, 246, 0.8)`,
                      `0 0 30px rgba(139, 92, 246, 0.5)`
                    ]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity
                  }}
                />

                <img
                  src={coasterImgSrc}
                  alt={coasterAlt}
                  className="w-full rounded-3xl shadow-2xl coaster-animated"
                  style={{
                    cursor: 'pointer',
                    animation: 'coasterFloat 3s ease-in-out infinite, coasterGlow 2s ease-in-out infinite alternate'
                  }}
                  onError={(e) => {
                    if (!e.target.src.includes('placeholder')) {
                      e.target.src = coasterFallback;
                    }
                  }}
                />
              </motion.div>
            ) : (
              /* DEAL CARD - V5 Style */
              <motion.div
                key="deal"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="rounded-xl overflow-hidden"
                style={{
                  backgroundColor: v5.colors.white,
                  border: `1px solid ${v5.colors.borderLight}`,
                  boxShadow: '0 8px 32px rgba(30, 27, 75, 0.15)',
                  borderLeft: `4px solid ${v5.colors.purple}`
                }}
              >
                {/* Business Image */}
                {business?.image_url && (
                  <div className="h-48 overflow-hidden">
                    <img
                      src={business.image_url}
                      alt={business.business_name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                <div className="p-6">
                  {/* Code Accent */}
                  <p style={{ fontFamily: v5.fonts.mono, fontSize: '12px', color: v5.colors.textHint, marginBottom: '8px' }}>
                    // deal_unlocked
                  </p>

                  {/* Business Name */}
                  <h2 style={{
                    fontFamily: "'Faustina', Georgia, serif",
                    fontSize: '24px',
                    fontWeight: 500,
                    color: v5.colors.textPrimary,
                    marginBottom: '8px'
                  }}>
                    {business?.business_name}
                  </h2>

                  {/* Distance/Location */}
                  {business?.distance_text && (
                    <div className="flex items-center gap-1.5 mb-4">
                      <MapPin size={14} style={{ color: v5.colors.green }} />
                      <span style={{ fontFamily: v5.fonts.body, fontSize: '14px', fontWeight: 500, color: v5.colors.green }}>
                        {business.distance_text}
                      </span>
                    </div>
                  )}
                  {!business?.distance_text && hub && (
                    <div className="flex items-center gap-1.5 mb-4">
                      <MapPin size={14} style={{ color: v5.colors.green }} />
                      <span style={{ fontFamily: v5.fonts.body, fontSize: '14px', fontWeight: 500, color: v5.colors.green }}>
                        Near {hub.name}
                      </span>
                    </div>
                  )}

                  {/* Offer Box */}
                  <div
                    className="p-4 rounded-xl mb-4"
                    style={{
                      background: `linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(249, 115, 22, 0.1) 100%)`,
                      border: `2px solid rgba(139, 92, 246, 0.2)`
                    }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Gift size={18} style={{ color: v5.colors.purple }} />
                      <p style={{
                        fontFamily: v5.fonts.body,
                        fontSize: '12px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        color: v5.colors.purple
                      }}>
                        Local Deal
                      </p>
                    </div>
                    <p style={{
                      fontFamily: "'Faustina', Georgia, serif",
                      fontSize: '18px',
                      fontWeight: 500,
                      color: v5.colors.textPrimary
                    }}>
                      {business?.deal_title}
                    </p>
                  </div>

                  {/* Social Proof */}

                  {/* CTA Button */}
                  {alreadyClaimed ? (
                    <Button
                      onClick={() => navigate('/deal-wallet')}
                      className="w-full rounded-xl py-5 text-base font-bold transition-all hover:-translate-y-0.5"
                      style={{
                        backgroundColor: v5.colors.green,
                        color: v5.colors.white,
                        fontFamily: v5.fonts.body
                      }}
                    >
                      View Deal Wallet
                    </Button>
                  ) : (
                    <Button
                      onClick={handleClaim}
                      disabled={claiming}
                      className="w-full rounded-xl py-5 text-base font-bold transition-all hover:-translate-y-0.5"
                      style={{
                        background: `linear-gradient(135deg, ${v5.colors.purple} 0%, ${v5.colors.purpleDark} 100%)`,
                        color: v5.colors.white,
                        fontFamily: v5.fonts.body,
                        boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)'
                      }}
                    >
                      {claiming ? 'Claiming...' : (isAuthenticated ? 'Claim Offer Now' : 'Sign Up to Claim Offer 🎉')}
                      {!claiming && isAuthenticated && <Star size={18} className="ml-2" />}
                    </Button>
                  )}

                  {/* Share Button */}
                  <motion.button
                    onClick={handleShareViaSMS}
                    className="w-full mt-3 py-3 flex items-center justify-center gap-2 rounded-xl transition-all"
                    style={{
                      backgroundColor: 'transparent',
                      color: v5.colors.textMuted,
                      border: `1px solid ${v5.colors.borderLight}`,
                      fontFamily: v5.fonts.body
                    }}
                    whileHover={{
                      backgroundColor: v5.colors.cream,
                      borderColor: v5.colors.purple,
                      color: v5.colors.purple
                    }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Send size={16} />
                    <span style={{ fontSize: '14px', fontWeight: 500 }}>
                      Not for you? Send to a friend
                    </span>
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Bottom Button - See All Offers */}
      {!showCoaster && (
        <div className="pb-4 px-6 pt-2">
          <button
            onClick={handleUnlockAllOffers}
            disabled={unlocking}
            className="w-full max-w-[400px] mx-auto flex items-center justify-center gap-3 px-8 py-4 rounded-xl font-bold transition-all hover:-translate-y-0.5"
            style={{
              background: `linear-gradient(135deg, ${v5.colors.orange} 0%, ${v5.colors.orangeDark} 100%)`,
              color: v5.colors.white,
              opacity: unlocking ? 0.7 : 1,
              fontFamily: v5.fonts.body,
              boxShadow: '0 4px 15px rgba(249, 115, 22, 0.4)'
            }}
          >
            <Unlock size={20} />
            {unlocking ? 'Unlocking...' : `See All Offers from ${hubName}`}
          </button>
        </div>
      )}
    </div>
  );
};

export default ScanNew;
